import React from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Container,
  Image,
  SimpleGrid,
  Card,
  CardBody,
  Badge,
  Icon,
  VStack,
  HStack,
} from "@chakra-ui/react";
import {
  FaComments,
  FaUsers,
  FaMicrophone,
  FaVideo,
  FaPoll,
  FaFileAlt,
  FaBroadcastTower,
  FaShieldAlt,
  FaRocket,
  FaHeart,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";

function LandingPage() {
  const navigate = useNavigate();

  return (
    <Box bg="gray.900" minH="100vh">
      <Header />

      {/* Hero Section */}
      <Box
        as="section"
        bg="gray.900"
        py="20"
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl">
          <Flex
            direction={{ base: "column", lg: "row" }}
            align="center"
            justify="space-between"
            gap={12}
          >
            {/* Left Side - Text Content */}
            <VStack
              spacing={8}
              flex="1"
              align={{ base: "center", lg: "start" }}
              textAlign={{ base: "center", lg: "left" }}
            >
              <VStack spacing={4} align={{ base: "center", lg: "start" }}>
                <Badge
                  colorScheme="teal"
                  px={4}
                  py={2}
                  borderRadius="full"
                  fontSize="sm"
                >
                  ✨ Welcome to the Future of Communication
                </Badge>
                <Heading
                  fontSize={["3xl", "4xl", "5xl", "6xl"]}
                  color="white"
                  lineHeight="shorter"
                  fontWeight="800"
                >
                  Create Amazing{" "}
                  <Text as="span" color="teal.400">
                    Spaces
                  </Text>{" "}
                  for Every Purpose
                </Heading>
                <Text fontSize={["lg", "xl"]} color="gray.300" maxW="600px">
                  Experience seamless communication with powerful spaces
                  designed for chat, polls, forms, broadcasts, and crystal-clear
                  audio/video calls. Build communities, gather feedback, and
                  connect instantly.
                </Text>
              </VStack>

              <HStack
                spacing={4}
                flexWrap="wrap"
                justify={{ base: "center", lg: "start" }}
              >
                <Button
                  size="lg"
                  colorScheme="teal"
                  px={8}
                  py={6}
                  fontSize="lg"
                  leftIcon={<FaRocket />}
                  onClick={() => navigate("/signup")}
                  _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
                  transition="all 0.3s"
                >
                  Start Creating Spaces
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="teal.400"
                  color="teal.400"
                  px={8}
                  py={6}
                  fontSize="lg"
                  _hover={{
                    bg: "teal.400",
                    color: "gray.900",
                    transform: "translateY(-2px)",
                  }}
                  transition="all 0.3s"
                  onClick={() => navigate("/login")}
                >
                  Explore Demo
                </Button>
              </HStack>

              {/* Stats */}
              <HStack spacing={8} pt={4}>
                <VStack spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" color="teal.400">
                    5+
                  </Text>
                  <Text fontSize="sm" color="gray.400">
                    Space Types
                  </Text>
                </VStack>
                <VStack spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" color="teal.400">
                    100%
                  </Text>
                  <Text fontSize="sm" color="gray.400">
                    Real-time
                  </Text>
                </VStack>
                <VStack spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" color="teal.400">
                    ∞
                  </Text>
                  <Text fontSize="sm" color="gray.400">
                    Possibilities
                  </Text>
                </VStack>
              </HStack>
            </VStack>

            {/* Right Side - Hero Image */}
            <Box flex="1" position="relative">
              <Box
                position="relative"
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="2xl"
              >
                <Image
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Smartphone showing communication app interface"
                  borderRadius="2xl"
                  objectFit="cover"
                  h={["300px", "400px", "500px"]}
                  w="100%"
                />
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  bg="linear-gradient(135deg, teal.500, purple.600)"
                  opacity={0.2}
                  borderRadius="2xl"
                />
              </Box>

              {/* Floating Cards */}
              <Card
                position="absolute"
                top="10%"
                right="-5%"
                bg="gray.800"
                borderColor="teal.500"
                borderWidth="1px"
                p={3}
                transform="rotate(12deg)"
                boxShadow="xl"
                display={{ base: "none", lg: "block" }}
              >
                <CardBody p={2}>
                  <HStack spacing={2}>
                    <Icon as={FaComments} color="teal.400" />
                    <Text color="white" fontSize="sm">
                      Chat Spaces
                    </Text>
                  </HStack>
                </CardBody>
              </Card>

              <Card
                position="absolute"
                bottom="15%"
                left="-5%"
                bg="gray.800"
                borderColor="purple.500"
                borderWidth="1px"
                p={3}
                transform="rotate(-8deg)"
                boxShadow="xl"
                display={{ base: "none", lg: "block" }}
              >
                <CardBody p={2}>
                  <HStack spacing={2}>
                    <Icon as={FaVideo} color="purple.400" />
                    <Text color="white" fontSize="sm">
                      Video Calls
                    </Text>
                  </HStack>
                </CardBody>
              </Card>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* Space Types Section */}
      <Box as="section" bg="gray.800" py="20">
        <Container maxW="container.xl">
          <VStack spacing={12} textAlign="center">
            <VStack spacing={4}>
              <Badge colorScheme="teal" px={4} py={2} borderRadius="full">
                Powerful Features
              </Badge>
              <Heading fontSize={["2xl", "3xl", "4xl"]} color="white">
                Choose Your Perfect{" "}
                <Text as="span" color="teal.400">
                  Space Type
                </Text>
              </Heading>
              <Text fontSize={["md", "lg"]} color="gray.300" maxW="600px">
                Every space is designed for a specific purpose. Pick the one
                that fits your needs and start building amazing experiences.
              </Text>
            </VStack>

            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 3 }}
              spacing={8}
              w="100%"
            >
              {/* Chat Space */}
              <Card
                bg="gray.700"
                borderWidth="1px"
                borderColor="gray.600"
                borderRadius="xl"
                p={6}
                _hover={{
                  borderColor: "teal.400",
                  transform: "translateY(-4px)",
                }}
                transition="all 0.3s"
              >
                <CardBody>
                  <VStack spacing={4} align="start">
                    <HStack>
                      <Icon as={FaComments} color="teal.400" boxSize={6} />
                      <Badge colorScheme="teal">Popular</Badge>
                    </HStack>
                    <Heading size="md" color="white">
                      Chat Spaces
                    </Heading>
                    <Text color="gray.300" fontSize="sm">
                      Real-time messaging with typing indicators, and threaded
                      conversations. Perfect for team collaboration and
                      community building.
                    </Text>
                    <Image
                      src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80"
                      alt="Mobile phone showing chat messages and conversations"
                      borderRadius="md"
                      h="150px"
                      w="100%"
                      objectFit="cover"
                    />
                    <HStack spacing={6} fontSize="xs" color="gray.400">
                      <Text>✓ Real-time messaging</Text>
                      <Text>✓ Typing indicators</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* Poll Space */}
              <Card
                bg="gray.700"
                borderWidth="1px"
                borderColor="gray.600"
                borderRadius="xl"
                p={6}
                _hover={{
                  borderColor: "blue.400",
                  transform: "translateY(-4px)",
                }}
                transition="all 0.3s"
              >
                <CardBody>
                  <VStack spacing={4} align="start">
                    <Icon as={FaPoll} color="blue.400" boxSize={6} />
                    <Heading size="md" color="white">
                      Poll Spaces
                    </Heading>
                    <Text color="gray.300" fontSize="sm">
                      Gather opinions and make decisions with interactive polls.
                      Real-time results and analytics help you understand your
                      audience.
                    </Text>
                    <Image
                      src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80"
                      alt="Digital dashboard with charts and voting results"
                      borderRadius="md"
                      h="150px"
                      w="100%"
                      objectFit="cover"
                    />
                    <HStack spacing={6} fontSize="xs" color="gray.400">
                      <Text>✓ Real-time results</Text>
                      <Text>✓ Analytics</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* Form Space */}
              <Card
                bg="gray.700"
                borderWidth="1px"
                borderColor="gray.600"
                borderRadius="xl"
                p={6}
                _hover={{
                  borderColor: "green.400",
                  transform: "translateY(-4px)",
                }}
                transition="all 0.3s"
              >
                <CardBody>
                  <VStack spacing={4} align="start">
                    <Icon as={FaFileAlt} color="green.400" boxSize={6} />
                    <Heading size="md" color="white">
                      Form Spaces
                    </Heading>
                    <Text color="gray.300" fontSize="sm">
                      Collect detailed information with custom forms. Support
                      for various field types, validation, and automated
                      responses.
                    </Text>
                    <Image
                      src="https://images.unsplash.com/photo-1586281380349-632531db7ed4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80"
                      alt="Person filling out online form on tablet device"
                      borderRadius="md"
                      h="150px"
                      w="100%"
                      objectFit="cover"
                    />
                    <HStack spacing={4} fontSize="xs" color="gray.400">
                      <Text>✓ Custom fields</Text>
                      <Text>✓ Validation</Text>
                      <Text>✓ Export data</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* Broadcast Space */}
              <Card
                bg="gray.700"
                borderWidth="1px"
                borderColor="gray.600"
                borderRadius="xl"
                p={6}
                _hover={{
                  borderColor: "orange.400",
                  transform: "translateY(-4px)",
                }}
                transition="all 0.3s"
              >
                <CardBody>
                  <VStack spacing={4} align="start">
                    <Icon
                      as={FaBroadcastTower}
                      color="orange.400"
                      boxSize={6}
                    />
                    <Heading size="md" color="white">
                      Broadcast Spaces
                    </Heading>
                    <Text color="gray.300" fontSize="sm">
                      Share announcements and updates with your audience.
                      Recipients can react with emojis to show engagement.
                    </Text>
                    <Image
                      src="https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80"
                      alt="Mobile phone showing broadcast message notification"
                      borderRadius="md"
                      h="150px"
                      w="100%"
                      objectFit="cover"
                    />
                    <HStack spacing={6} fontSize="xs" color="gray.400">
                      <Text>✓ One-way messaging</Text>
                      <Text>✓ Emoji reactions</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* Audio Calls */}
              <Card
                bg="gray.700"
                borderWidth="1px"
                borderColor="gray.600"
                borderRadius="xl"
                p={6}
                _hover={{
                  borderColor: "purple.400",
                  transform: "translateY(-4px)",
                }}
                transition="all 0.3s"
              >
                <CardBody>
                  <VStack spacing={4} align="start">
                    <Icon as={FaMicrophone} color="purple.400" boxSize={6} />
                    <Heading size="md" color="white">
                      Audio Calls
                    </Heading>
                    <Text color="gray.300" fontSize="sm">
                      Crystal-clear voice calls with minimal latency. Perfect
                      for quick discussions and voice-only meetings.
                    </Text>
                    <Image
                      src="https://images.unsplash.com/photo-1484704849700-f032a568e944?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80"
                      alt="Professional headphones for high-quality audio calls"
                      borderRadius="md"
                      h="150px"
                      w="100%"
                      objectFit="cover"
                    />
                    <HStack spacing={6} fontSize="xs" color="gray.400">
                      <Text>✓ HD audio quality</Text>
                      <Text>✓ Low latency</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>

              {/* Video Calls */}
              <Card
                bg="gray.700"
                borderWidth="1px"
                borderColor="gray.600"
                borderRadius="xl"
                p={6}
                _hover={{
                  borderColor: "pink.400",
                  transform: "translateY(-4px)",
                }}
                transition="all 0.3s"
              >
                <CardBody>
                  <VStack spacing={4} align="start">
                    <HStack>
                      <Icon as={FaVideo} color="pink.400" boxSize={6} />
                    </HStack>
                    <Heading size="md" color="white">
                      Video Calls
                    </Heading>
                    <Text color="gray.300" fontSize="sm">
                      Face-to-face conversations with high-quality video.
                      Connect visually with your team for more engaging
                      communication.
                    </Text>
                    <Image
                      src="https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80"
                      alt="Video conference call on laptop screen"
                      borderRadius="md"
                      h="150px"
                      w="100%"
                      objectFit="cover"
                    />
                    <HStack spacing={6} fontSize="xs" color="gray.400">
                      <Text>✓ HD video quality</Text>
                      <Text>✓ Low latency</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Features Section */}
      <Box as="section" bg="gray.900" py="20">
        <Container maxW="container.xl">
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={16}
            alignItems="center"
          >
            {/* Left - Image */}
            <Box>
              <Image
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
                alt="Communication app interface on multiple devices"
                borderRadius="2xl"
                boxShadow="2xl"
                h={["300px", "400px"]}
                w="100%"
                objectFit="cover"
              />
            </Box>

            {/* Right - Content */}
            <VStack spacing={8} align="start">
              <VStack spacing={4} align="start">
                <Badge colorScheme="teal" px={4} py={2} borderRadius="full">
                  Why Choose Vartaverse?
                </Badge>
                <Heading fontSize={["2xl", "3xl", "4xl"]} color="white">
                  Built for Modern{" "}
                  <Text as="span" color="teal.400">
                    Communication
                  </Text>
                </Heading>
                <Text fontSize="lg" color="gray.300">
                  Experience the next generation of communication tools designed
                  for teams, communities, and individuals who value seamless
                  interaction.
                </Text>
              </VStack>

              <VStack spacing={6} align="stretch" w="100%">
                <HStack spacing={4}>
                  <Icon as={FaShieldAlt} color="teal.400" boxSize={6} />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" color="white">
                      Secure & Private
                    </Text>
                    <Text color="gray.300" fontSize="sm">
                      End-to-end encryption keeps your conversations safe
                    </Text>
                  </VStack>
                </HStack>

                <HStack spacing={4}>
                  <Icon as={FaRocket} color="teal.400" boxSize={6} />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" color="white">
                      Lightning Fast
                    </Text>
                    <Text color="gray.300" fontSize="sm">
                      Real-time messaging with minimal latency worldwide
                    </Text>
                  </VStack>
                </HStack>

                <HStack spacing={4}>
                  <Icon as={FaUsers} color="teal.400" boxSize={6} />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" color="white">
                      Team Focused
                    </Text>
                    <Text color="gray.300" fontSize="sm">
                      Built specifically for team collaboration and productivity
                    </Text>
                  </VStack>
                </HStack>

                <HStack spacing={4}>
                  <Icon as={FaHeart} color="teal.400" boxSize={6} />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" color="white">
                      User Friendly
                    </Text>
                    <Text color="gray.300" fontSize="sm">
                      Intuitive interface that anyone can use immediately
                    </Text>
                  </VStack>
                </HStack>
              </VStack>

              <Button
                size="lg"
                colorScheme="teal"
                px={8}
                leftIcon={<FaRocket />}
                onClick={() => navigate("/signup")}
                _hover={{ transform: "translateY(-2px)" }}
                transition="all 0.3s"
              >
                Get Started Today
              </Button>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box as="section" bg="teal.500" py="20">
        <Container maxW="container.lg" textAlign="center">
          <VStack spacing={8}>
            <VStack spacing={4}>
              <Heading fontSize={["2xl", "3xl", "4xl"]} color="white">
                Ready to Transform Your Communication?
              </Heading>
              <Text fontSize={["md", "lg"]} color="teal.50" maxW="600px">
                Join thousands of teams who have already discovered the power of
                Vartaverse spaces. Start building amazing experiences today.
              </Text>
            </VStack>

            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Button
                size="lg"
                bg="white"
                color="teal.500"
                px={8}
                py={6}
                fontSize="lg"
                leftIcon={<FaRocket />}
                onClick={() => navigate("/signup")}
                _hover={{ bg: "gray.100", transform: "translateY(-2px)" }}
                transition="all 0.3s"
              >
                Create Your First Space
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                px={8}
                py={6}
                fontSize="lg"
                _hover={{
                  bg: "white",
                  color: "teal.500",
                  transform: "translateY(-2px)",
                }}
                transition="all 0.3s"
                onClick={() => navigate("/login")}
              >
                Sign In
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}

export default LandingPage;
